import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Container, Row, Col, Form, Collapse } from 'react-bootstrap';
import { ChevronDown, MapPin, Search, Briefcase, Clock, DollarSign, Award, Calendar, Layers, User, GraduationCap, ChevronLeft, ChevronRight } from 'lucide-react';
import './FindJobs.css';

const FindJobs = () => {
    const location = useLocation();
    const [jobs, setJobs] = useState([]);
    const [filteredJobs, setFilteredJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- Temporaty State ---
    const [tempKeywords, setTempKeywords] = useState('');
    const [tempLocation, setTempLocation] = useState('');
    const [tempDates, setTempDates] = useState([]);
    const [tempVacancy, setTempVacancy] = useState([]);
    const [tempSpecialism, setTempSpecialism] = useState([]);
    const [tempSalary, setTempSalary] = useState([]);
    const [tempCareer, setTempCareer] = useState([]);
    const [tempExperience, setTempExperience] = useState([]);
    const [tempGender, setTempGender] = useState([]);
    const [tempIndustry, setTempIndustry] = useState([]);
    const [tempQualification, setTempQualification] = useState([]);

    // --- Autocompleate State ---
    const [availableTitles, setAvailableTitles] = useState([]);
    const [availableLocations, setAvailableLocations] = useState([]);
    const [filteredTitleSuggestions, setFilteredTitleSuggestions] = useState([]);
    const [filteredLocSuggestions, setFilteredLocSuggestions] = useState([]);
    const [showTitleSuggestions, setShowTitleSuggestions] = useState(false);
    const [showLocSuggestions, setShowLocSuggestions] = useState(false);

    // --- State for pagination ---
    const [currentPage, setCurrentPage] = useState(1);
    const jobsPerPage = 10; 

    const [openFilters, setOpenFilters] = useState({
        date: false, vacancy: false, specialism: false, offeredSalary: false,
        careerLevel: false, experience: false, gender: false, industry: false, qualification: false
    });

    // 1. Fetch
    useEffect(() => {
        const fetchJobs = async () => {
            try {
                //const response = await fetch('http://localhost:5000/api-portal/jobs');
                const response = await fetch('/api-portal/jobs');
                const data = await response.json();
                setJobs(data);

                // --- URL reading logic ---
                const params = new URLSearchParams(location.search);
                const keywordParam = params.get('keyword') || '';
                const locationParam = params.get('location') || '';

                setTempKeywords(keywordParam);
                setTempLocation(locationParam);

                let results = data;
                if (keywordParam) {
                    results = results.filter(j => 
                        j.title.toLowerCase().includes(keywordParam.toLowerCase()) ||
                        j.description.toLowerCase().includes(keywordParam.toLowerCase())
                    );
                }
                if (locationParam) {
                    results = results.filter(j => j.location.toLowerCase().includes(locationParam.toLowerCase()));
                }

                setFilteredJobs(results);

                setAvailableTitles([...new Set(data.map(j => j.title))]);
                setAvailableLocations([...new Set(data.map(j => j.location))]);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching jobs:", error);
                setLoading(false);
            }
        };
        fetchJobs();
    }, [location.search]);

    // 2. Logic for title suggestion
    useEffect(() => {
        if (tempKeywords.trim().length > 0) {
            const filtered = availableTitles.filter(t => 
                t.toLowerCase().includes(tempKeywords.toLowerCase())
            );
            setFilteredTitleSuggestions(filtered);
            if(document.activeElement.name === "keywordInput") setShowTitleSuggestions(true);
        }
    }, [tempKeywords, availableTitles]);

    // 3. Logic for location suggestion
    useEffect(() => {
        if (tempLocation.trim().length > 0) {
            const filtered = availableLocations.filter(l => 
                l.toLowerCase().includes(tempLocation.toLowerCase())
            );
            setFilteredLocSuggestions(filtered);
        } else {
            setFilteredLocSuggestions([]);
        }
    }, [tempLocation, availableLocations]);

    // 4. Main function for filtering
    const applyFilters = () => {
        let results = [...jobs];

        if (tempKeywords) {
            results = results.filter(j => 
                j.title.toLowerCase().includes(tempKeywords.toLowerCase()) ||
                j.description.toLowerCase().includes(tempKeywords.toLowerCase())
            );
        }

        if (tempLocation) {
            results = results.filter(j => j.location.toLowerCase().includes(tempLocation.toLowerCase()));
        }

        if (tempVacancy.length > 0) results = results.filter(j => tempVacancy.includes(j.type));
        if (tempSpecialism.length > 0) results = results.filter(j => tempSpecialism.includes(j.category));
        if (tempSalary.length > 0) results = results.filter(j => tempSalary.includes(j.salary));
        if (tempCareer.length > 0) results = results.filter(j => tempCareer.includes(j.careerLevel));
        if (tempExperience.length > 0) results = results.filter(j => tempExperience.includes(j.experience));
        if (tempGender.length > 0) results = results.filter(j => tempGender.includes(j.gender));
        if (tempIndustry.length > 0) results = results.filter(j => tempIndustry.includes(j.industry));
        if (tempQualification.length > 0) results = results.filter(j => tempQualification.includes(j.qualification));

        if (tempDates.length > 0 && !tempDates.includes('All')) {
            const now = new Date();
            results = results.filter(j => {
                const jobDate = new Date(j.createdAt);
                const diffHours = (now - jobDate) / (1000 * 60 * 60);
                if (tempDates.includes('Last Hour') && diffHours <= 1) return true;
                if (tempDates.includes('Last 24 Hours') && diffHours <= 24) return true;
                if (tempDates.includes('Last 7 days') && diffHours <= 168) return true;
                if (tempDates.includes('Last 14 days') && diffHours <= 336) return true;
                if (tempDates.includes('Last 30 days') && diffHours <= 720) return true;
                return false;
            });
        }

        setFilteredJobs(results);
        setCurrentPage(1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCheck = (val, currentList, setter) => {
        const newList = currentList.includes(val) ? currentList.filter(i => i !== val) : [...currentList, val];
        setter(newList);
    };

    const toggleFilter = (filter) => {
        setOpenFilters(prev => ({ ...prev, [filter]: !prev[filter] }));
    };

    if (loading) return <div className="text-center py-5">Loading jobs...</div>;

    // --- Calculate number of jobs per page logic ---
    const indexOfLastJob = currentPage * jobsPerPage;
    const indexOfFirstJob = indexOfLastJob - jobsPerPage;
    const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
    const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);

    // Funkcija for chaning pages
    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <div className="find-jobs-container">
            <Container className="px-4 py-5">
                <Row className="gx-5">
                    <Col lg={4} className="pe-lg-5">
                        <div className="filter-sidebar p-4 shadow-sm rounded bg-white">
                            <h4 className="mb-4 fw-bold">Filter Search</h4>

                            {/* Job title autocompleate -------------------------------------*/}
                            <div className="filter-group mb-4 position-relative">
                                <label className="filter-label">Search Keywords</label>
                                <div className="search-box-vertical">
                                    <Search className="search-icon" size={18} />
                                    <input 
                                        type="text"
                                        name="keywordInput" 
                                        placeholder="Job title..." 
                                        value={tempKeywords}
                                        onChange={(e) => {
                                            setTempKeywords(e.target.value);
                                            // Turn on suggestion when typing
                                            setShowTitleSuggestions(e.target.value.length > 0);
                                        }}
                                        onFocus={() => tempKeywords.length > 0 && setShowTitleSuggestions(true)}
                                        onBlur={() => setTimeout(() => setShowTitleSuggestions(false), 200)}
                                    />
                                </div>
                                {showTitleSuggestions && filteredTitleSuggestions.length > 0 && (
                                    <div className="search-suggestions-dropdown sidebar-dropdown shadow-lg animate-in">
                                        {filteredTitleSuggestions.map((title, idx) => (
                                            <div key={idx} className="suggestion-item" onClick={() => { 
                                                setTempKeywords(title); 
                                                setShowTitleSuggestions(false); 
                                            }}>
                                                <Briefcase size={14} className="me-2 text-primary" /> {title}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Location Autocompleate */}
                            <div className="filter-group mb-4 position-relative">
                                <label className="filter-label">Location</label>
                                <div className="search-box-vertical">
                                    <MapPin className="search-icon" size={18} />
                                    <input 
                                        type="text" 
                                        placeholder="City or state" 
                                        value={tempLocation}
                                        onChange={(e) => {
                                            setTempLocation(e.target.value);
                                            // Turn on suggestion when typing
                                            setShowLocSuggestions(e.target.value.length > 0);
                                        }}
                                        onFocus={() => tempLocation.length > 0 && setShowLocSuggestions(true)}
                                        onBlur={() => setTimeout(() => setShowLocSuggestions(false), 200)}
                                    />
                                </div>
                                {showLocSuggestions && filteredLocSuggestions.length > 0 && (
                                    <div className="search-suggestions-dropdown sidebar-dropdown shadow-lg animate-in">
                                        {filteredLocSuggestions.map((loc, idx) => (
                                            <div key={idx} className="suggestion-item" onClick={() => { 
                                                setTempLocation(loc); 
                                                setShowLocSuggestions(false); 
                                            }}>
                                                <MapPin size={14} className="me-2 text-primary" /> {loc}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Dynamic section for filter */}
                            {/* 1. Date Posted */}
                            <FilterSection title="Date Posted" id="date" isOpen={openFilters.date} toggle={toggleFilter}>
                                {['Last Hour', 'Last 24 Hours', 'Last 7 days', 'Last 14 days', 'Last 30 days', 'All'].map(opt => (
                                    <Form.Check 
                                        key={opt} 
                                        type="checkbox" 
                                        label={opt} 
                                        checked={tempDates.includes(opt)} 
                                        onChange={() => handleCheck(opt, tempDates, setTempDates)} 
                                        className="custom-checkbox mb-2" 
                                    />
                                ))}
                            </FilterSection>

                            {/* 2. Vacancy Type */}
                            <FilterSection title="Vacancy Type" id="vacancy" isOpen={openFilters.vacancy} toggle={toggleFilter}>
                                {['Freelance', 'Full Time', 'Contract'].map(opt => (
                                    <Form.Check 
                                        key={opt} 
                                        type="checkbox" 
                                        label={opt} 
                                        checked={tempVacancy.includes(opt)} 
                                        onChange={() => handleCheck(opt, tempVacancy, setTempVacancy)} 
                                        className="custom-checkbox mb-2" 
                                    />
                                ))}
                            </FilterSection>

                            {/* 3. Specialism (Category) */}
                            <FilterSection title="Specialism" id="specialism" isOpen={openFilters.specialism} toggle={toggleFilter}>
                                {['Accounting', 'Administrative Assistants', 'CDL A and B Drivers', 'Warehouse Associates', 'Forklift Operators', 'General Labor'].map(opt => (
                                    <Form.Check 
                                        key={opt} 
                                        type="checkbox" 
                                        label={opt} 
                                        checked={tempSpecialism.includes(opt)} 
                                        onChange={() => handleCheck(opt, tempSpecialism, setTempSpecialism)} 
                                        className="custom-checkbox mb-2" 
                                    />
                                ))}
                            </FilterSection>

                            {/* 4. Offered Salary */}
                            <FilterSection title="Offered Salary" id="offeredSalary" isOpen={openFilters.offeredSalary} toggle={toggleFilter}>
                                {['0 - $15,000', '$15,000 - $20,000', '$20,000 - $30,000', '$30,000 - $50,000', '$50,000+'].map(opt => (
                                    <Form.Check 
                                        key={opt} 
                                        type="checkbox" 
                                        label={opt} 
                                        checked={tempSalary.includes(opt)} 
                                        onChange={() => handleCheck(opt, tempSalary, setTempSalary)} 
                                        className="custom-checkbox mb-2" 
                                    />
                                ))}
                            </FilterSection>

                            {/* 5. Career Level */}
                            <FilterSection title="Career Level" id="careerLevel" isOpen={openFilters.careerLevel} toggle={toggleFilter}>
                                {['Manager', 'Officer', 'Student', 'Executive', 'Others'].map(opt => (
                                    <Form.Check 
                                        key={opt} 
                                        type="checkbox" 
                                        label={opt} 
                                        checked={tempCareer.includes(opt)} 
                                        onChange={() => handleCheck(opt, tempCareer, setTempCareer)} 
                                        className="custom-checkbox mb-2" 
                                    />
                                ))}
                            </FilterSection>

                            {/* 6. Experience */}
                            <FilterSection title="Experience" id="experience" isOpen={openFilters.experience} toggle={toggleFilter}>
                                {['Fresh', 'Less than 1 Year', '2 Years', '3 Years', '4 Years', '5 Years', '6 Years', '7 Years', '8 Years +'].map(opt => (
                                    <Form.Check 
                                        key={opt} 
                                        type="checkbox" 
                                        label={opt} 
                                        checked={tempExperience.includes(opt)} 
                                        onChange={() => handleCheck(opt, tempExperience, setTempExperience)} 
                                        className="custom-checkbox mb-2" 
                                    />
                                ))}
                            </FilterSection>

                            {/* 7. Gender */}
                            <FilterSection title="Gender" id="gender" isOpen={openFilters.gender} toggle={toggleFilter}>
                                {['Male', 'Female', 'Both'].map(opt => (
                                    <Form.Check 
                                        key={opt} 
                                        type="checkbox" 
                                        label={opt} 
                                        checked={tempGender.includes(opt)} 
                                        onChange={() => handleCheck(opt, tempGender, setTempGender)} 
                                        className="custom-checkbox mb-2" 
                                    />
                                ))}
                            </FilterSection>

                            {/* 8. Industry */}
                            <FilterSection title="Industry" id="industry" isOpen={openFilters.industry} toggle={toggleFilter}>
                                {['Development', 'Management', 'Finance', 'Html Department', 'Seo', 'Banking', 'Designing'].map(opt => (
                                    <Form.Check 
                                        key={opt} 
                                        type="checkbox" 
                                        label={opt} 
                                        checked={tempIndustry.includes(opt)} 
                                        onChange={() => handleCheck(opt, tempIndustry, setTempIndustry)} 
                                        className="custom-checkbox mb-2" 
                                    />
                                ))}
                            </FilterSection>

                            {/* 9. Qualification */}
                            <FilterSection title="Qualification" id="qualification" isOpen={openFilters.qualification} toggle={toggleFilter}>
                                {['Certificate', 'Diploma', 'Associate Degree', 'Bachelor Degree', 'Master’s Degree'].map(opt => (
                                    <Form.Check 
                                        key={opt} 
                                        type="checkbox" 
                                        label={opt} 
                                        checked={tempQualification.includes(opt)} 
                                        onChange={() => handleCheck(opt, tempQualification, setTempQualification)} 
                                        className="custom-checkbox mb-2" 
                                    />
                                ))}
                            </FilterSection>

                            {/* Main button for filtering */}
                            <button className="btn-find-jobs-sidebar w-100 mt-4" onClick={applyFilters}>
                                Update Results
                            </button>
                        </div>
                    </Col>

                    {/* Right side: Results */}
                    <Col lg={8}>
                        <div className="results-header d-flex justify-content-between align-items-center mb-4">
                            <p className="mb-0 text-muted">
                               Showing <strong>{indexOfFirstJob + 1}-{Math.min(indexOfLastJob, filteredJobs.length)}</strong> of <strong>{filteredJobs.length}</strong> Jobs
                            </p>
                        </div>

                        <div className="jobs-list">
                            {currentJobs.length > 0 ? (
                                currentJobs.map(job => (
                                    <div key={job.id} className="job-card-premium mb-4 animate-in">
                                        <div className="d-flex flex-column">
                                            {/* 1. Title */}
                                            <h5 className="job-card-title mb-1">{job.title}</h5>

                                            {/* 2. Category */}
                                            <div className="category-label mb-3">
                                                <Briefcase size={14} className="me-1 text-primary" />
                                                <span className="text-primary fw-600">{job.category}</span>
                                            </div>

                                            {/* 3. Metadata */}
                                            <div className="job-card-meta mb-3">
                                                <span className="salary-text"><DollarSign size={14} /> {job.salary}</span>
                                                <span><Clock size={14} /> {job.type}</span>
                                                <span><Layers size={14} /> {job.careerLevel}</span>
                                                <span><Award size={14} /> {job.experience}</span>
                                                <span><User size={14} /> {job.gender}</span>
                                                <span><GraduationCap size={14} /> {job.qualification}</span>
                                            </div>

                                            {/* 4. Location */}
                                            <div className="location-info mb-2">
                                                <MapPin size={16} className=" me-2" />
                                                <span className="fw-500">{job.location}</span>
                                            </div>

                                            {/* 5. Date */}
                                            <div className="date-info d-flex align-items-center">
                                                <Calendar size={16} className="me-2" />
                                                Posted on: {new Date(job.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </div>

                                            {/* View details button */}
                                            <div className="mt-2 d-flex">
                                                <Link to={`/job/${job.id}`} className="btn-view-job text-decoration-none">
                                                    View Details
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="no-results text-center py-5">
                                    <Search size={50} className="text-muted mb-3 opacity-25" />
                                    <h5 className="text-muted">No jobs found.</h5>
                                    <p className="text-muted small">Try adjusting your filters or search keywords.</p>
                                </div>
                            )}
                        </div>

                        {/* --- Pagination UI --- */}
                        {totalPages > 1 && (
                            <div className="pagination-wrapper d-flex justify-content-center mt-5">
                                <button 
                                    className="page-control-btn" 
                                    onClick={() => paginate(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <ChevronLeft size={20} />
                                </button>

                                {[...Array(totalPages)].map((_, index) => (
                                    <button
                                        key={index + 1}
                                        onClick={() => paginate(index + 1)}
                                        className={`page-number-btn ${currentPage === index + 1 ? 'active' : ''}`}
                                    >
                                        {index + 1}
                                    </button>
                                ))}

                                <button 
                                    className="page-control-btn" 
                                    onClick={() => paginate(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    <ChevronRight size={20} />
                                </button>
                            </div>
                        )}

                    </Col>
                </Row>
            </Container>
        </div>
    );
};

// Helper function component for section filter
const FilterSection = ({ title, id, isOpen, toggle, children }) => (
    <div className="mb-3">
        <div className="filter-header" onClick={() => toggle(id)}>
            <span className="fw-bold">{title}</span>
            <ChevronDown className={`chevron ${isOpen ? 'rotate' : ''}`} size={18} />
        </div>
        <Collapse in={isOpen}>
            <div className="filter-content pt-3">{children}</div>
        </Collapse>
        <hr className="my-2" />
    </div>
);

export default FindJobs;